/**
 * TRUS-567 — per-report toggles for the Report Library.
 *
 * Each Report Library entry has a `reports.library.<report-id>` row in
 * page_capabilities, so a super admin can switch it off for one org in
 * Admin -> Page Access Management. This module turns a bulk page-access result
 * into a filtered catalog.
 *
 * It FAILS OPEN: an entry is only hidden when the backend explicitly says
 * has_access === false for a real reason (org override, role override,
 * default disabled). A missing result (loading, network error, the migration
 * not applied yet -> page_not_found) keeps the entry visible, so a blip never
 * blanks the Reports page.
 */

import type { ReportCategory } from './ReportLibraryPanel';

export type ReportAccessMap = Record<string, { has_access?: boolean; access_source?: string } | undefined>;

export const REPORT_LIBRARY_KEY_PREFIX = 'reports.library.';

export const reportLibraryPageKey = (reportId: string) => `${REPORT_LIBRARY_KEY_PREFIX}${reportId}`;

/** Access sources that mean "no decision was made" rather than "switched off". */
const NON_DECISIONS = new Set(['page_not_found', 'not_found', 'parent_capability_disabled']);

export const isReportHidden = (access: ReportAccessMap | null | undefined, reportId: string): boolean => {
  const r = access?.[reportLibraryPageKey(reportId)];
  if (!r || r.has_access !== false) return false;
  return !NON_DECISIONS.has(r.access_source ?? '');
};

/** Drop switched-off entries, then any category left empty. */
export function filterReportCatalog(
  catalog: ReportCategory[],
  access: ReportAccessMap | null | undefined,
): ReportCategory[] {
  if (!access) return catalog;
  return catalog
    .map((c) => ({ ...c, items: c.items.filter((i) => !isReportHidden(access, i.id)) }))
    .filter((c) => c.items.length > 0);
}

/** Every page key the catalog needs checked. */
export const reportLibraryPageKeys = (catalog: ReportCategory[]): string[] =>
  catalog.flatMap((c) => c.items.map((i) => reportLibraryPageKey(i.id)));
